import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { Calendar as CalendarIcon, MapPin, ArrowLeft, Check, X, Share2, Shield } from "lucide-react";
import { toast } from "sonner";
import VerifiedBadge from "@/components/VerifiedBadge";

export default function EventDetail() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/events/${id}`); setEv(data); }
    catch (err) { console.error("Failed to load event:", err); toast.error("Gagal memuat event"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const rsvp = async (status) => {
    try { await api.post(`/events/${id}/rsvp`, { status }); toast.success(status === "going" ? "Kamu terdaftar" : "Kamu tidak hadir"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  if (!ev) return <Layout><div className="text-bizma-subtext">Memuat...</div></Layout>;
  const d = new Date(ev.date);

  return (
    <Layout>
      <Link to="/app/events" className="inline-flex items-center gap-2 text-sm text-bizma-subtext mb-6" data-testid="back-to-events">
        <ArrowLeft size={16}/> Kembali ke Event
      </Link>
      <div className="grid lg:grid-cols-3 gap-6" data-testid="event-detail">
        <div className="lg:col-span-2 felt-card overflow-hidden">
          <div className="aspect-video bg-bizma-muted">
            {ev.photo_path ? <img src={fileUrl(ev.photo_path)} alt="" className="w-full h-full object-cover" /> :
              <div className="w-full h-full grid place-items-center text-bizma-subtext"><CalendarIcon size={40}/></div>}
          </div>
          <div className="p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold flex items-center gap-2">
              <CalendarIcon size={14}/> {d.toLocaleDateString("id-ID", { weekday:"long", day: "numeric", month: "long", year: "numeric" })} • {d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}
            </div>
            <h1 className="text-3xl font-display font-bold mt-2 flex items-center gap-2 flex-wrap">
              {ev.title}
              {ev.is_pengurus_only && <span className="text-[10px] uppercase tracking-widest bg-bizma-terracotta text-white font-bold px-2 py-1 rounded-full inline-flex items-center gap-1" data-testid="event-pengurus-badge"><Shield size={10}/> Kopdar Pengurus</span>}
            </h1>
            {ev.location && <div className="mt-2 text-bizma-subtext flex items-center gap-1"><MapPin size={14}/> {ev.location}</div>}
            <p className="mt-4 text-[#4A4743] leading-relaxed whitespace-pre-line">{ev.description}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => rsvp("going")}
                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors ${ev.my_rsvp === "going" ? "bg-bizma-sage text-white" : "bg-white border border-bizma-border text-bizma-ink hover:bg-bizma-muted"}`}
                data-testid="rsvp-going-btn"><Check size={16}/> Saya Hadir</button>
              <button onClick={() => rsvp("not_going")}
                className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold transition-colors ${ev.my_rsvp === "not_going" ? "bg-bizma-terracotta text-white" : "bg-white border border-bizma-border text-bizma-ink hover:bg-bizma-muted"}`}
                data-testid="rsvp-not-going-btn"><X size={16}/> Tidak Bisa</button>
              <button onClick={() => {
                const url = `${window.location.origin}/app/events/${ev.event_id}`;
                const msg = `📅 *${ev.title}*\n${d.toLocaleString("id-ID",{dateStyle:"long",timeStyle:"short"})}${ev.location?`\n📍 ${ev.location}`:""}\n\n${ev.description}\n\n${url}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
              }} className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold bg-[#25D366] text-white hover:bg-[#1DA851]" data-testid="event-share-wa-btn"><Share2 size={16}/> Bagikan ke WhatsApp</button>
            </div>
          </div>
        </div>

        <div className="felt-card p-6" data-testid="event-attendees">
          <div className="text-xs uppercase tracking-widest text-bizma-sage font-bold">Yang Akan Hadir ({(ev.going || []).length})</div>
          <div className="mt-4 space-y-3">
            {(ev.going || []).length === 0 && <div className="text-sm text-bizma-subtext">Belum ada yang RSVP.</div>}
            {(ev.going || []).map((u) => (
              <Link key={u.user_id} to={`/app/members/${u.user_id}`} className="flex items-center gap-3 group" data-testid={`attendee-${u.user_id}`}>
                {u.photo_path ? <img src={fileUrl(u.photo_path)} alt="" className="w-10 h-10 rounded-full object-cover" /> :
                  <div className="w-10 h-10 rounded-full bg-bizma-sage text-white grid place-items-center font-semibold">{u.name?.charAt(0)}</div>}
                <div className="flex items-center gap-1 group-hover:text-bizma-terracotta transition-colors">
                  <span className="font-medium">{u.name}</span>
                  {u.is_verified && <VerifiedBadge size={14}/>}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
